// =============================================
// Simulation Dashboard — לוח בקרת סימולציה
// Kipikid — מערכת נוכחות ילדים בגנים
// =============================================

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Play, Square, SkipForward, Clock, Phone, Zap,
  Activity, AlertTriangle, Users, Building2,
  Plus, X, Trash2, ExternalLink
} from 'lucide-react';
import { API_BASE_URL } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
// Note: Simulation uses a distinct red theme not shared with other modules


interface SimulationStatus {
  active: boolean;
  currentTime: string;
  realTime: string;
  ratio: number;
  startedAt?: string;
  simulatedStartTime?: string;
  endTime?: string;
  testPhones: string[];
}

interface LogEntry {
  timestamp: string;
  realTimestamp: string;
  action: string;
  details: string;
}

const colors = {
  primary: '#dc2626',
  primaryLight: '#fef2f2',
  primaryDark: '#991b1b',
  accent: '#f59e0b',
  accentLight: '#fffbeb',
  text: '#1f2937',
  textLight: '#6b7280',
  border: '#e5e7eb',
  background: '#f9fafb',
  success: '#16a34a',
  teal: '#0d9488',
};

const ratioOptions = [1, 5, 10, 30, 60];

function formatTime(isoStr: string): string {
  try {
    const d = new Date(isoStr);
    return d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch { return '--:--:--'; }
}

function formatTimeShort(isoStr: string): string {
  try {
    const d = new Date(isoStr);
    return d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
  } catch { return '--:--'; }
}

async function simApi(endpoint: string, method: 'GET' | 'POST' = 'GET', body?: object) {
  const token = useAuthStore.getState().token || '';
  const res = await fetch(`${API_BASE_URL}/simulation/${endpoint}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const text = await res.text();
  if (!text) return { success: false, error: `Empty response (HTTP ${res.status})` };
  try {
    return JSON.parse(text);
  } catch {
    return { success: false, error: `Invalid JSON (HTTP ${res.status}): ${text.substring(0, 200)}` };
  }
}

export default function SimulationDashboard() {
  // State
  const [status, setStatus] = useState<SimulationStatus | null>(null);
  const [log, setLog] = useState<LogEntry[]>([]);
  const [startTime, setStartTime] = useState('07:00');
  const [endTime, setEndTime] = useState('10:00');
  const [ratio, setRatio] = useState(10);
  const [testPhones, setTestPhones] = useState<string[]>(['']);
  const [jumpTime, setJumpTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    document.title = 'קיפיקיד — סימולציה';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#dc2626');
  }, []);

  // Poll status
  const fetchStatus = useCallback(async () => {
    try {
      const res = await simApi('status');
      if (res.success && res.data) setStatus(res.data);
    } catch { /* silent */ }
  }, []);

  const fetchLog = useCallback(async () => {
    try {
      const res = await simApi('log?last=50');
      if (res.success && res.data) setLog(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchLog();
    pollRef.current = setInterval(() => {
      fetchStatus();
      fetchLog();
    }, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchStatus, fetchLog]);

  // Actions
  const handleStart = async () => {
    setLoading(true);
    setError('');
    try {
      const phones = testPhones.filter(p => p.trim().length > 0);
      const res = await simApi('start', 'POST', {
        startTime,
        endTime: endTime || undefined,
        ratio,
        testPhones: phones.length > 0 ? phones : undefined,
      });
      if (res.success) {
        setStatus(res.data);
      } else {
        setError(res.message || 'Failed to start simulation');
      }
    } catch (ex: any) {
      setError(ex.message || 'Error');
    }
    setLoading(false);
  };

  const handleStop = async () => {
    setLoading(true);
    const res = await simApi('stop', 'POST');
    if (res.success) setStatus(res.data);
    setLoading(false);
  };

  const handleJump = async () => {
    if (!jumpTime) return;
    setLoading(true);
    const res = await simApi('set-time', 'POST', { time: jumpTime });
    if (res.success) setStatus(res.data);
    setJumpTime('');
    setLoading(false);
  };

  const handleTrigger = async (fn: string) => {
    setLoading(true);
    const res = await simApi(`trigger/${fn}`, 'POST');
    if (res.success) {
      await fetchLog();
    } else {
      setError(res.message || `Failed to trigger ${fn}`);
    }
    setLoading(false);
  };

  const handleUpdatePhones = async () => {
    const phones = testPhones.filter(p => p.trim().length > 0);
    await simApi('test-phones', 'POST', { phones });
    await fetchStatus();
  };

  const isActive = status?.active === true;

  const quickLinks = [
    { label: 'הורה', icon: Users, path: '/parent/login', color: '#3b82f6' },
    { label: 'גננת (טאבלט)', icon: Building2, path: '/tablet/login', color: '#8b5cf6' },
    { label: 'מוקד חרום', icon: Phone, path: '/callcenter/login', color: colors.teal },
    { label: 'מנהל מערכת', icon: Zap, path: '/sysadmin/login', color: '#f59e0b' },
  ];

  return (
    <div dir="rtl" style={{ minHeight: '100vh', backgroundColor: '#111827', color: 'white' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #991b1b 0%, #dc2626 50%, #f59e0b 100%)',
        padding: '24px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '12px',
            backgroundColor: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Activity size={28} color="white" />
          </div>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: 'bold', margin: 0 }}>Simulation Control</h1>
            <p style={{ fontSize: '13px', opacity: 0.8, margin: 0 }}>KipiKid — לוח בקרת סימולציה</p>
          </div>
        </div>

        {/* Live clock */}
        <div style={{ textAlign: 'left' }}>
          {isActive && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '12px', padding: '10px 20px',
            }}>
              <div style={{
                width: '10px', height: '10px', borderRadius: '50%',
                backgroundColor: '#ef4444', animation: 'pulse 1s infinite',
              }} />
              <span style={{ fontSize: '28px', fontWeight: 'bold', fontFamily: 'monospace' }}>
                {status ? formatTime(status.currentTime) : '--:--:--'}
              </span>
              <span style={{ fontSize: '14px', opacity: 0.7 }}>×{status?.ratio || 1}</span>
            </div>
          )}
          {!isActive && (
            <div style={{
              backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '12px', padding: '10px 20px',
            }}>
              <span style={{ fontSize: '14px', opacity: 0.7 }}>לא פעיל</span>
            </div>
          )}
        </div>
      </div>

      {/* Error */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            style={{
              margin: '16px 24px 0', padding: '12px 16px',
              backgroundColor: '#fef2f2', borderRadius: '10px',
              display: 'flex', alignItems: 'center', gap: '8px',
            }}
          >
            <AlertTriangle size={18} color="#dc2626" />
            <span style={{ color: '#dc2626', fontSize: '14px', flex: 1 }}>{error}</span>
            <button onClick={() => setError('')} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
            }}><X size={16} color="#dc2626" /></button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Grid */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px',
        padding: '24px', maxWidth: '1200px', margin: '0 auto',
      }}>
        {/* Left Column — Settings */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Simulation Settings */}
          <Card title="הגדרות סימולציה" icon={<Clock size={18} />}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={labelStyle}>שעת התחלה</label>
                <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
                  disabled={isActive} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>שעת סיום</label>
                <input type="time" value={endTime} onChange={e => {
                  const val = e.target.value;
                  const hour = parseInt(val.split(':')[0]);
                  if (hour >= 14) {
                    // תיקון אוטומטי: 21:35→09:35, 22:00→10:00 (PM→AM)
                    const corrected = `${String(hour - 12).padStart(2, '0')}:${val.split(':')[1]}`;
                    setEndTime(corrected);
                    return;
                  }
                  setEndTime(val);
                }}
                  disabled={isActive} style={inputStyle} />
                {endTime && parseInt(endTime.split(':')[0]) >= 12 && (
                  <p style={{ fontSize: '11px', color: '#f59e0b', marginTop: '2px' }}>
                    ⚠️ {endTime} = {parseInt(endTime.split(':')[0]) > 12 ? `${parseInt(endTime.split(':')[0]) - 12}:${endTime.split(':')[1]}` : endTime} אחה״צ — האם התכוונת לבוקר?
                  </p>
                )}
              </div>
            </div>

            <div style={{ marginTop: '12px' }}>
              <label style={labelStyle}>יחס מהירות</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {ratioOptions.map(r => (
                  <button key={r} onClick={() => setRatio(r)} disabled={isActive}
                    style={{
                      padding: '8px 16px', borderRadius: '8px', border: 'none',
                      backgroundColor: ratio === r ? '#dc2626' : '#374151',
                      color: 'white', fontWeight: ratio === r ? 'bold' : 'normal',
                      cursor: isActive ? 'not-allowed' : 'pointer', fontSize: '14px',
                      opacity: isActive ? 0.5 : 1,
                    }}>
                    ×{r}
                  </button>
                ))}
              </div>
              <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                ×{ratio}: כל שניה אמיתית = {ratio} שניות מדומות
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              {!isActive ? (
                <button onClick={handleStart} disabled={loading} style={{
                  ...btnStyle, backgroundColor: '#16a34a',
                  opacity: loading ? 0.6 : 1,
                }}>
                  <Play size={18} /> התחל סימולציה
                </button>
              ) : (
                <button onClick={handleStop} disabled={loading} style={{
                  ...btnStyle, backgroundColor: '#dc2626',
                  opacity: loading ? 0.6 : 1,
                }}>
                  <Square size={18} /> עצור
                </button>
              )}
            </div>
          </Card>

          {/* Jump to time */}
          {isActive && (
            <Card title="קפיצה לשעה" icon={<SkipForward size={18} />}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <input type="time" value={jumpTime} onChange={e => setJumpTime(e.target.value)}
                    style={inputStyle} placeholder="HH:mm" />
                </div>
                <button onClick={handleJump} disabled={!jumpTime || loading}
                  style={{ ...btnStyle, backgroundColor: '#f59e0b', opacity: !jumpTime ? 0.5 : 1 }}>
                  <SkipForward size={16} /> קפוץ
                </button>
              </div>
            </Card>
          )}

          {/* Test Phones */}
          <Card title="מספרי טסט" icon={<Phone size={18} />}>
            {testPhones.map((phone, i) => (
              <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                <input
                  type="tel" value={phone}
                  onChange={e => {
                    const next = [...testPhones];
                    next[i] = e.target.value;
                    setTestPhones(next);
                  }}
                  placeholder="050-1234567"
                  style={{ ...inputStyle, flex: 1 }}
                />
                {testPhones.length > 1 && (
                  <button onClick={() => setTestPhones(testPhones.filter((_, j) => j !== i))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                    <Trash2 size={16} color="#ef4444" />
                  </button>
                )}
              </div>
            ))}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setTestPhones([...testPhones, ''])}
                style={{ ...btnSmallStyle, backgroundColor: '#374151' }}>
                <Plus size={14} /> הוסף מספר
              </button>
              <button onClick={handleUpdatePhones}
                style={{ ...btnSmallStyle, backgroundColor: '#0d9488' }}>
                עדכן
              </button>
            </div>
            <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
              הודעות SMS/WhatsApp יועברו למספרים אלו במקום למספרים האמיתיים
            </p>
          </Card>

          {/* Quick Login Links */}
          <Card title="כניסה מהירה" icon={<ExternalLink size={18} />}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {quickLinks.map(link => (
                <a key={link.path} href={link.path} target="_blank" rel="noopener noreferrer"
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '12px', borderRadius: '10px',
                    backgroundColor: link.color + '20', color: link.color,
                    textDecoration: 'none', fontSize: '14px', fontWeight: '500',
                    border: `1px solid ${link.color}30`,
                    transition: 'all 0.2s',
                  }}>
                  <link.icon size={18} />
                  {link.label}
                </a>
              ))}
            </div>
          </Card>
        </div>

        {/* Right Column — Actions + Log */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Manual Triggers */}
          <Card title="פעולות ידניות" icon={<Zap size={18} />}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button onClick={() => handleTrigger('daily-prep')} disabled={loading}
                style={{ ...btnStyle, backgroundColor: '#7c3aed', justifyContent: 'flex-start' }}>
                <Zap size={16} /> הרץ הכנה יומית (DailyPrep)
              </button>
              <button onClick={() => handleTrigger('check-missing')} disabled={loading}
                style={{ ...btnStyle, backgroundColor: '#dc2626', justifyContent: 'flex-start' }}>
                <AlertTriangle size={16} /> הרץ בדיקת חסרים (CheckMissing)
              </button>
              <button onClick={() => handleTrigger('cleanup')} disabled={loading}
                style={{ ...btnStyle, backgroundColor: '#6b7280', justifyContent: 'flex-start' }}>
                <Trash2 size={16} /> הרץ ניקוי (Cleanup)
              </button>
            </div>
          </Card>

          {/* Status */}
          {status && (
            <Card title="מצב נוכחי" icon={<Activity size={18} />}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px' }}>
                <StatusRow label="סטטוס" value={isActive ? 'פעיל' : 'כבוי'}
                  color={isActive ? '#16a34a' : '#6b7280'} />
                <StatusRow label="שעה מדומה" value={formatTime(status.currentTime)} />
                <StatusRow label="שעה אמיתית" value={formatTime(status.realTime)} />
                <StatusRow label="יחס" value={`×${status.ratio}`} />
                {status.simulatedStartTime && (
                  <StatusRow label="התחלה" value={formatTimeShort(status.simulatedStartTime)} />
                )}
                {status.endTime && (
                  <StatusRow label="סיום" value={formatTimeShort(status.endTime)} />
                )}
                {status.testPhones?.length > 0 && (
                  <StatusRow label="טלפונים" value={status.testPhones.join(', ')} />
                )}
              </div>
            </Card>
          )}

          {/* Event Log */}
          <Card title="לוג אירועים" icon={<Activity size={18} />}>
            <div style={{
              maxHeight: '400px', overflowY: 'auto',
              display: 'flex', flexDirection: 'column', gap: '4px',
            }}>
              {log.length === 0 && (
                <p style={{ color: '#6b7280', fontSize: '13px', textAlign: 'center', padding: '20px' }}>
                  אין אירועים עדיין
                </p>
              )}
              {[...log].reverse().map((entry, i) => (
                <div key={i} style={{
                  padding: '8px 12px', borderRadius: '8px',
                  backgroundColor: '#1f2937', fontSize: '13px',
                  borderRight: `3px solid ${
                    entry.action === 'Error' ? '#ef4444'
                    : entry.action === 'CheckMissing' ? '#f59e0b'
                    : entry.action === 'DailyPreparation' ? '#8b5cf6'
                    : '#3b82f6'
                  }`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                    <span style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '12px' }}>
                      {formatTime(entry.timestamp)}
                    </span>
                    <span style={{ color: '#6b7280', fontSize: '11px' }}>
                      {entry.action}
                    </span>
                  </div>
                  <span style={{ color: '#d1d5db' }}>{entry.details}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}

// ==========================================
// Sub-components
// ==========================================

function Card({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{
      backgroundColor: '#1f2937', borderRadius: '16px', padding: '20px',
      border: '1px solid #374151',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        marginBottom: '16px', color: '#d1d5db',
      }}>
        {icon}
        <h3 style={{ fontSize: '15px', fontWeight: '600', margin: 0 }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

function StatusRow({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
      <span style={{ color: '#9ca3af' }}>{label}</span>
      <span style={{ color: color || '#e5e7eb', fontWeight: '500', fontFamily: 'monospace' }}>
        {value}
      </span>
    </div>
  );
}

// ==========================================
// Styles
// ==========================================

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '13px', color: '#9ca3af', marginBottom: '4px',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', borderRadius: '8px',
  border: '1px solid #374151', backgroundColor: '#111827',
  color: 'white', fontSize: '14px', outline: 'none',
  boxSizing: 'border-box',
};

const btnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '8px',
  padding: '12px 20px', borderRadius: '10px', border: 'none',
  color: 'white', fontSize: '14px', fontWeight: '600',
  cursor: 'pointer', width: '100%', justifyContent: 'center',
};

const btnSmallStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '4px',
  padding: '6px 12px', borderRadius: '8px', border: 'none',
  color: 'white', fontSize: '13px', cursor: 'pointer',
};
